// ABOUTME: SPEC-26 Phase 5 — the orientation lock's visible half: refuse the wrong orientation.
// ABOUTME: Not an authored Figma frame (there is no plate for either state); built from reel tokens.

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Tablet } from 'lucide-react';
import { useViewportSize, useDeviceProfile } from './Projectionist/useViewportSize';
import {
  orientationToPromptFor, requiredOrientation, isTabletClass,
  requestOrientationLock, releaseOrientationLock,
} from '../utils/orientationLock';

// Deliberately generic strings, overridable as props: this surface is platform chrome, not brand
// chrome, and AC-10 keeps brand vocabulary out of anything shared. A second tenant gets it for free.
export const DEFAULT_TITLE = 'Please rotate your device';
export const MESSAGE = {
  portrait: 'This experience is designed for portrait.',
  landscape: 'This experience is designed for landscape.',
};

// The glyph mimes the gesture being asked for, ending on the target orientation: upright for a
// phone, on its side for a tablet. Suppressed under `prefers-reduced-motion`, where a looping
// rotation is exactly the kind of vestibular trigger the setting exists to stop.
const KEYFRAMES = {
  portrait: [-90, -90, 0, 0],
  landscape: [0, 0, -90, -90],
};

/**
 * Blocks the experience while a device is held the wrong way, and asks for the right way back.
 *
 * Renders `null` — not a hidden element — whenever it is not blocking, so a tenant with the lock
 * disabled gets byte-identical markup to before it existed (AC-9), and a desktop guest is never one
 * CSS mistake away from a full-screen overlay.
 *
 * @param {{ enabled?: boolean, title?: string, message?: string }} props
 */
function OrientationGate({ enabled = false, title = DEFAULT_TITLE, message }) {
  const viewport = useViewportSize();
  const profile = useDeviceProfile();
  const panelRef = useRef(null);

  // What this device is held to at all (regardless of how it is being held right now), and what —
  // if anything — to ask for at this moment. The first drives the native lock, the second the UI.
  const required = enabled ? requiredOrientation({ viewport, profile }) : null;
  const promptFor = orientationToPromptFor({ viewport, profile, enabled });

  // Ask for the real lock as soon as we know the device class, not once it has already been turned
  // the wrong way: where the platform grants it (installed / standalone) the guest then never sees
  // this overlay at all, which is the outcome the client actually asked for. Everywhere else it
  // resolves false and costs one no-op call per mount.
  //
  // Release only a lock this component actually took. `unlock()` returns the screen to the
  // document's default orientation, and undoing a lock we were never granted is not obviously a
  // no-op on a boot path that remounts this component (App.js returns from three different
  // branches, and StrictMode double-invokes effects in dev).
  useEffect(() => {
    if (!required) return undefined;
    let active = true;
    let held = false;
    requestOrientationLock(required).then((granted) => {
      held = granted;
      // Unmounted while the request was still in flight — release whatever it ended up taking.
      if (granted && !active) releaseOrientationLock();
    });
    return () => {
      active = false;
      if (held) releaseOrientationLock();
    };
  }, [required]);

  // Pull focus out of whatever is behind the overlay, and put it back when the device is turned
  // round. The overlay is opaque and covers the viewport, so pointers are already handled; this is
  // the keyboard/screen-reader half of the same idea. Restoring matters as much as taking: rotating
  // mid-sentence with the text field open must not silently drop the caret and close the keyboard.
  useEffect(() => {
    if (!promptFor) return undefined;
    const previouslyFocused = document.activeElement;
    if (panelRef.current) panelRef.current.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function'
          && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [promptFor]);

  if (!promptFor) return null;

  const Glyph = isTabletClass(viewport, profile) ? Tablet : Smartphone;

  return (
    <div
      ref={panelRef}
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      data-orientation-required={promptFor}
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center gap-4 text-center outline-none"
      style={{
        // Opaque, not frosted: the composition behind it is being rendered at a shape it was never
        // authored for, and that is precisely what must not be visible. Same gradient as the boot
        // loader (reel.loader.gradientFrom/To) so the two dark full-screen beats match.
        background: 'radial-gradient(ellipse at center, rgba(48,48,48,1) 0%, rgba(27,27,27,1) 100%)',
        paddingTop: 'var(--reel-safe-t)',
        paddingRight: 'var(--reel-safe-r)',
        paddingBottom: 'var(--reel-safe-b)',
        paddingLeft: 'var(--reel-safe-l)',
      }}
    >
      <motion.div
        aria-hidden="true"
        animate={profile.reducedMotion ? { rotate: 0 } : { rotate: KEYFRAMES[promptFor] }}
        transition={profile.reducedMotion
          ? undefined
          : { duration: 2.4, times: [0, 0.35, 0.65, 1], repeat: Infinity, repeatDelay: 0.6, ease: 'easeInOut' }}
      >
        <Glyph className="w-10 h-10 text-reel-chrome-textPrimary" strokeWidth={1.25} />
      </motion.div>
      <p className="font-reel-heading text-reel-chrome-textPrimary text-lg tracking-wide px-6">
        {title}
      </p>
      <p className="font-reel-body text-reel-chrome-textDim text-sm px-8 max-w-sm">
        {message || MESSAGE[promptFor]}
      </p>
    </div>
  );
}

export default OrientationGate;
